import type { SQLiteDBConnection } from "@capacitor-community/sqlite";
import { CapacitorSQLite, SQLiteConnection } from "@capacitor-community/sqlite";

import type { Promisable } from "@core/foundation/promise";
import { multiline } from "@core/foundation/string";

export type SqliteOperation = (
  db: SQLiteDBConnection,
) => Promisable<{ readonly applied: boolean }>;

export class SqliteClient {
  private readonly database: string;

  // @mutable
  private dbPromise: Promisable<SQLiteDBConnection> | undefined = undefined;

  private readonly tables: readonly string[];

  // @mutable
  private transactionQueue: Promisable<unknown> = undefined;

  private readonly version: number;

  public constructor({
    database,
    tables,
    version,
  }: {
    readonly database: string;
    readonly tables: readonly string[];
    readonly version: number;
  }) {
    this.database = database;
    this.tables = tables;
    this.version = version;
  }

  public connect(): Promisable<SQLiteDBConnection> {
    this.dbPromise ??= (async () => {
      const sqlite = new SQLiteConnection(CapacitorSQLite);

      await sqlite.checkConnectionsConsistency();

      const db = await sqlite.createConnection(
        this.database,
        false,
        "no-encryption",
        this.version,
        false,
      );

      await db.open();

      await Promise.all(
        this.tables.map(async table => {
          await this.createTable(table, db);
        }),
      );

      return db;
    })();

    return this.dbPromise;
  }

  public async transact(
    operations: readonly SqliteOperation[],
  ): Promise<{ readonly committed: boolean }> {
    const previousTransaction = this.transactionQueue;
    const result = (async () => {
      await previousTransaction;

      return this.runTransaction(operations);
    })();

    this.transactionQueue = result.catch(() => undefined);

    return result;
  }

  private async createTable(
    tableName: string,
    db: SQLiteDBConnection,
  ): Promise<void> {
    await db.execute(multiline`
      CREATE TABLE IF NOT EXISTS ${tableName} (
        PK TEXT NOT NULL,
        SK TEXT NOT NULL,
        SK2 TEXT,
        data TEXT NOT NULL,
        sqlite_created_at TEXT NOT NULL,
        sqlite_updated_at TEXT NOT NULL,
        PRIMARY KEY (PK, SK)
      )
    `);

    // oxlint-disable-next-line no-warning-comments
    // TODO(APT-2405): remove once every device table holds the SK2 column.
    const { values = [] } = await db.query(`PRAGMA table_info(${tableName})`);
    const columns: readonly { readonly name: string }[] = values;

    if (!columns.some(({ name }) => name === "SK2")) {
      await db.execute(`ALTER TABLE ${tableName} ADD COLUMN SK2 TEXT`);
    }

    await db.execute(multiline`
      CREATE INDEX IF NOT EXISTS ${tableName}_SK2
      ON ${tableName} (PK, SK2)
      WHERE SK2 IS NOT NULL
    `);
  }

  private async runTransaction(
    operations: readonly SqliteOperation[],
  ): Promise<{ readonly committed: boolean }> {
    const db = await this.connect();

    await db.beginTransaction();

    try {
      for (const operation of operations) {
        const { applied } = await operation(db);

        if (!applied) {
          await db.rollbackTransaction();

          return { committed: false };
        }
      }

      await db.commitTransaction();

      return { committed: true };
    } catch (error) {
      await db.rollbackTransaction();

      throw error;
    }
  }
}
